// Browser-native agent screen recorder.
// Captures the screen (+ optional system & mic audio) via getDisplayMedia and
// streams timesliced WebM chunks to the backend, which appends them to a single
// file on the self-hosted server's disk. The agent grants a ONE-TIME screen
// share per shift; recording then auto starts/stops as the caller flips
// `shouldRecord` for the enabled modes.
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const QUALITY_BITRATE = { low: 600_000, medium: 1_500_000, high: 3_000_000 };

function pickMime() {
  const cands = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  for (const c of cands) {
    try {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) return c;
    } catch {}
  }
  return "video/webm";
}

// Mix the display (system) audio track with the mic into ONE track so both are
// captured (MediaRecorder records a single audio track).
function buildStream(displayStream, micStream) {
  const videoTracks = displayStream.getVideoTracks();
  const sysAudio = displayStream.getAudioTracks();
  const micAudio = micStream ? micStream.getAudioTracks() : [];
  if (sysAudio.length + micAudio.length <= 1) {
    // Nothing to mix — return the display stream (already has 0/1 audio track),
    // adding the mic track if the display had none.
    const out = new MediaStream(videoTracks);
    const a = sysAudio[0] || micAudio[0];
    if (a) out.addTrack(a);
    return { stream: out, ctx: null };
  }
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const dest = ctx.createMediaStreamDestination();
  if (sysAudio.length) ctx.createMediaStreamSource(new MediaStream([sysAudio[0]])).connect(dest);
  if (micAudio.length) ctx.createMediaStreamSource(new MediaStream([micAudio[0]])).connect(dest);
  const out = new MediaStream(videoTracks);
  dest.stream.getAudioTracks().forEach((t) => out.addTrack(t));
  return { stream: out, ctx };
}

export function useScreenRecorder() {
  const [streamReady, setStreamReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState(null);

  const displayRef = useRef(null);   // raw getDisplayMedia stream
  const micRef = useRef(null);       // raw mic stream
  const audioCtxRef = useRef(null);
  const recorderRef = useRef(null);
  const sessionRef = useRef(null);   // { id, startedAt, callId }
  const wantAudioRef = useRef(true);

  const teardownStream = useCallback(() => {
    try { displayRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    try { micRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    try { audioCtxRef.current?.close(); } catch {}
    displayRef.current = null;
    micRef.current = null;
    audioCtxRef.current = null;
    setStreamReady(false);
  }, []);

  // Request screen-share consent (must be called from a user gesture).
  // Returns { ok, reason?, surface? } — reason 'declined' | 'wrong_surface'.
  const requestConsent = useCallback(async ({ withAudio = true, requireFullScreen = true } = {}) => {
    setError(null);
    wantAudioRef.current = withAudio;
    let display;
    try {
      display = await navigator.mediaDevices.getDisplayMedia({
        // Hint the picker to default to the whole screen and hide the
        // tab/window switcher so agents are steered to "Entire Screen".
        video: { frameRate: 8, displaySurface: "monitor" },
        audio: withAudio,
        monitorTypeSurfaces: "include",
        surfaceSwitching: "exclude",
        selfBrowserSurface: "exclude",
        systemAudio: withAudio ? "include" : "exclude",
      });
    } catch (e) {
      setError(e?.message || "Screen share was declined");
      setStreamReady(false);
      return { ok: false, reason: "declined" };
    }
    // Enforce ENTIRE-SCREEN capture: reject window/tab shares. displaySurface
    // is undefined on some browsers (e.g. Firefox) — only reject when we can
    // actually read it, otherwise we'd block browsers that can't report it.
    const vtrack = display.getVideoTracks()[0];
    const surface = vtrack?.getSettings?.().displaySurface;
    if (requireFullScreen && surface && surface !== "monitor") {
      try { display.getTracks().forEach((t) => t.stop()); } catch {}
      setStreamReady(false);
      return { ok: false, reason: "wrong_surface", surface };
    }
    displayRef.current = display;
    if (withAudio) {
      try {
        micRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch { micRef.current = null; }
    }
    // If the agent stops sharing via the browser bar, reset so we re-prompt.
    display.getVideoTracks().forEach((t) => {
      t.onended = () => teardownStream();
    });
    setStreamReady(true);
    return { ok: true, surface: surface || "unknown" };
  }, [teardownStream]);

  const startSession = useCallback(async ({ mode, callId, campaignId, quality }) => {
    if (!displayRef.current || recorderRef.current) return;
    try {
      const r = await api.post("/me/screen-recording/start", {
        mode, call_id: callId || null, campaign_id: campaignId || null,
      });
      const sessionId = r.data?.session_id;
      if (!sessionId) return;
      sessionRef.current = { id: sessionId, startedAt: Date.now(), callId: callId || null };

      const { stream, ctx } = buildStream(displayRef.current, wantAudioRef.current ? micRef.current : null);
      audioCtxRef.current = ctx;
      const rec = new MediaRecorder(stream, {
        mimeType: pickMime(),
        videoBitsPerSecond: QUALITY_BITRATE[quality] || QUALITY_BITRATE.medium,
      });
      rec.ondataavailable = async (ev) => {
        if (!ev.data || ev.data.size === 0 || !sessionRef.current) return;
        const fd = new FormData();
        fd.append("session_id", sessionRef.current.id);
        fd.append("file", ev.data, "chunk.webm");
        try {
          await api.post("/me/screen-recording/chunk", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (e) { /* keep recording; a dropped chunk is non-fatal */ }
      };
      recorderRef.current = rec;
      rec.start(5000); // 5s timeslice → periodic chunk upload
      setRecording(true);
    } catch (e) {
      setError(e?.message || "Could not start recording");
    }
  }, []);

  const stopSession = useCallback(async (callId) => {
    const rec = recorderRef.current;
    const sess = sessionRef.current;
    recorderRef.current = null;
    sessionRef.current = null;
    setRecording(false);
    if (!rec) return;
    try {
      await new Promise((resolve) => {
        rec.onstop = resolve;
        try { rec.stop(); } catch { resolve(); }
      });
    } catch {}
    if (sess) {
      const dur = Math.round((Date.now() - sess.startedAt) / 1000);
      try {
        await api.post("/me/screen-recording/stop", {
          session_id: sess.id,
          duration_seconds: dur,
          call_id: callId || sess.callId || null,
        });
      } catch {}
    }
  }, []);

  useEffect(() => () => {
    try { recorderRef.current?.stop(); } catch {}
    teardownStream();
  }, [teardownStream]);

  return {
    streamReady, recording, error,
    requestConsent, startSession, stopSession, teardownStream,
    isRecording: () => !!recorderRef.current,
  };
}
